import { defineStore } from 'pinia'
import { tools } from '@/compositions/tools'
import { LevelStatus } from '@/types/enums'

const { toHumanNumber, numberFormat, cloneDeep } = tools

export interface Token {
  id: string
  name: string
  token: string // is set when is valid for jwt_decode()
  valid: string
  __token: string
  games?: Record<Games.GameKeyType, string>
  com?: boolean
  email?: string
  [key: string]: string | undefined | Record<string, string> | boolean
}

export interface GameStoreState {
  tokens: { [key: string]: Token }
  loyalty: Games.GamesLoyalty
  logBans: { [key: string]: string }
  cart: { [key: string]: Games.Offer[] }
  cartMinVal: { [key: string]: number }
  abTests: Record<string, string>
}

const cartCapacityValue = 6

function _getGameCartMap(cartState: { [key: string]: Games.Offer[] }, game: Games.GameKeyLax) {
  if (!game)
    return {}
  return Object.fromEntries((cartState?.[game] || []).map((item, index) => [item.id, index]))
}

function _getGameCartSumVal(cartState: { [key: string]: Games.Offer[] }, game: Games.GameKeyLax) {
  if (!game)
    return 0
  return (cartState?.[game] || []).reduce(
    (sum, offer) => sum + (offer?.discount?.value || offer?.price?.value || 0) * (offer?.qty || 1),
    0
  )
}

export const useGameStore = defineStore('storeGame', () => {
  const tokens = ref<{ [key: string]: Token }>({})
  const loyalty = ref<Games.GamesLoyalty>({})
  const logBans = ref<{ [key: string]: string }>({})
  const cart = ref<{ [key: string]: Games.Offer[] }>({})
  const cartMinVal = ref<{ [key: string]: number }>({})
  const abTests = ref<Record<string, string>>({})
  const devApiMode = ref<Record<string, 'dev' | 'timeshift' | 'local'>>({})

  // Sync devApiMode across browser tabs in real time.
  // The 'storage' event fires only in OTHER tabs, but updating devApiMode.value with
  // a new object reference (from JSON.parse) triggers Vue reactivity → pinia persist
  // writes → the first tab gets a storage event → loop. Guard with a JSON.stringify
  // equality check so we only update (and re-persist) when the value actually changed.
  if (!import.meta.env.SSR && import.meta.env.VITE_PROD !== 'on') {
    window.addEventListener('storage', (event) => {
      if (event.key === 'storeGame' && event.newValue) {
        try {
          const stored = JSON.parse(event.newValue)
          if (
            stored.devApiMode
            && JSON.stringify(stored.devApiMode) !== JSON.stringify(devApiMode.value)
          ) {
            devApiMode.value = stored.devApiMode
          }
        } catch {}
      }
    })
  }

  const cartCapacity = cartCapacityValue

  // --- Getters ---

  function token(game: string | null | undefined): Token | null {
    if (!game)
      return null
    return tokens.value?.[game] || null
  }

  const getTokens = computed(() => tokens.value || {})

  function loyaltyFor(gameKey: Games.GameKeyLax): Games.Loyalty | null {
    if (!gameKey)
      return null
    if (tokens.value?.[gameKey]?.id === String(loyalty.value?.[gameKey]?.id) && loyalty.value?.[gameKey]?.id) {
      return loyalty.value[gameKey] || null
    }
    return null
  }

  function logBan(game: Games.GameKeyLax): string | undefined {
    if (!game)
      return undefined
    return logBans.value?.[game]
  }

  const abTestSettings = computed(() => abTests.value || {})

  function gameCart(game: Games.GameKeyLax): Games.Offer[] {
    if (!game)
      return []
    return cart.value?.[game] || []
  }

  function gameCartMap(game: Games.GameKeyLax): Record<string | number, number> {
    return _getGameCartMap(cart.value, game)
  }

  function gameCartMinVal(game: Games.GameKeyLax): number {
    if (!game)
      return 0
    if (tokens.value?.[game]?.id === String(loyalty.value?.[game]?.id) && loyalty.value?.[game]?.id) {
      const data = loyalty.value[game] || null
      if (data) {
        return data.minPurchaseCur || 0
      }
    }
    return 0
  }

  function gameCartSumVal(game: Games.GameKeyLax): number {
    return _getGameCartSumVal(cart.value, game)
  }

  function gameCartSumValHuman(game: Games.GameKeyLax): string {
    const sum = _getGameCartSumVal(cart.value, game)
    if (!sum) {
      return ''
    }
    return `${game ? (loyalty.value[game]?.cur || '') : ''} ${numberFormat(sum || 0)}`
  }

  function gameCartPaymentAvailable(game: Games.GameKeyLax, addedOfferValue = 0): boolean {
    return _getGameCartSumVal(cart.value, game) + addedOfferValue >= gameCartMinVal(game)
  }

  // --- Actions (former mutations) ---

  function setToken(game: string, tokenData: Token) {
    tokens.value[game] = tokenData
  }

  function clearTokens() {
    tokens.value = {}
  }

  function logOutAll() {
    tokens.value = {}
    loyalty.value = {}
    cart.value = {}
    logBans.value = {}
  }

  function logOut(game: string) {
    if (tokens.value?.[game]) {
      delete tokens.value[game]
      if (game === 'goat') {
        loyalty.value = {}
      } else {
        delete loyalty.value[game as Games.GameKeyType]
      }
      if (cart.value && game) {
        cart.value[game] = []
      }
    }
  }

  function setLoyalty(gameKey: Games.GameKeyType, apiLoyaltyData: Games.Lax<Games.ApiLoyaltyData>) {
    if (apiLoyaltyData?.id && String(apiLoyaltyData.id) === tokens.value?.[gameKey]?.id) {
      loyalty.value[gameKey] = {
        id: apiLoyaltyData?.id || 0,
        energy: apiLoyaltyData?.energy || 0,
        gems: apiLoyaltyData?.gems || 0,
        loyalPct: apiLoyaltyData?.loyalPct || 0,
        loyalStatus: apiLoyaltyData?.loyalStatus || 0,
        loyalStatusNext: apiLoyaltyData?.loyalStatusNext || 0,
        energyHuman: toHumanNumber(apiLoyaltyData?.energy || 0),
        gemsHuman: toHumanNumber(apiLoyaltyData?.gems || 0),
        levelStatus: (LevelStatus[apiLoyaltyData?.loyalStatus || 0] ?? 'SILVER').toLowerCase(),
        nextLevelStatus: (LevelStatus[apiLoyaltyData?.loyalStatusNext || 0] ?? 'SILVER').toLowerCase(),
        cur: apiLoyaltyData?.cur || '',
        minPurchaseCur: apiLoyaltyData?.minPurchaseCur || 0,
        minPurchaseCurHuman: `${apiLoyaltyData?.cur || ''} ${numberFormat(apiLoyaltyData?.minPurchaseCur || 0)}`,
        name: apiLoyaltyData?.name || '',
        avatar: apiLoyaltyData?.avatar || '',
        goatId: apiLoyaltyData?.goatId || 0,
        leaguePoints: apiLoyaltyData?.leaguePoints || 0,
        leagueName: apiLoyaltyData?.leagueName || '',
        leagueAssetLogo: apiLoyaltyData?.leagueAssetLogo || '',
        purchasesToCollect: apiLoyaltyData?.purchasesToCollect || 0
      }
    } else {
      delete loyalty.value[gameKey]
    }
  }

  function setLogBan(gameKey: Games.GameKeyType, until: string) {
    logBans.value[gameKey] = until
  }

  function setAbTests(abTestSetting: Record<string, string> = {}) {
    abTests.value = abTestSetting
  }

  function addToGameCart(offer: Games.Offer) {
    const cartMap = _getGameCartMap(cart.value, offer.gameKey)
    const copyOffer = cloneDeep(offer)
    if (copyOffer.multiOffers) {
      delete copyOffer.multiOffers
    }
    if (copyOffer.id) {
      if (!cart.value[copyOffer.gameKey]) {
        cart.value[copyOffer.gameKey] = []
      }
      copyOffer.qty = 1
      if (cart.value?.[copyOffer?.gameKey]?.[cartMap?.[copyOffer?.id]] !== undefined) {
        const currentQty = cart.value?.[copyOffer?.gameKey]?.[cartMap[copyOffer.id]]?.qty ?? 1
        if (copyOffer.allowQty && (copyOffer.purchaseLimit ?? 1) > currentQty) {
          const stateCart = cart.value?.[copyOffer?.gameKey]?.[cartMap?.[copyOffer?.id]]
          if (stateCart) {
            stateCart.qty = currentQty + 1
          }
        }
      } else if ((cart.value?.[copyOffer?.gameKey]?.length ?? 0) < cartCapacityValue) {
        cart.value?.[copyOffer?.gameKey]?.push(copyOffer)
      }
    }
  }

  /**
   * if index it is updated from cart lvl;
   * if index === undefined it is updated after refresh offer list from api;
   * offerData have to contains id;
   */
  function updateGameCartOffer(game: string, offerData: Games.Offer, index: number | undefined) {
    const cartIndex = index ?? _getGameCartMap(cart.value, offerData.gameKey)?.[offerData?.id ?? ''] ?? -1
    if (
      cart.value?.[game]?.[cartIndex] &&
      cart.value[game][cartIndex]?.id &&
      cart.value[game][cartIndex].id === offerData?.id
    ) {
      const copyOfferData = cloneDeep(offerData)
      cart.value[game][cartIndex] = { ...cart.value[game][cartIndex], ...copyOfferData }
    }
  }

  function clearGameCart(game: string) {
    if (game) {
      cart.value[game] = []
    }
  }

  function removeFromGameCart(game: string, index: number) {
    if (cart.value?.[game]?.[index] !== undefined) {
      cart.value[game].splice(index, 1)
    }
  }

  function setCartGameMinVal(gameKey: Games.GameKeyType, minVal: number) {
    cartMinVal.value[gameKey] = minVal
  }

  return {
    // state
    tokens,
    loyalty,
    logBans,
    cart,
    cartMinVal,
    abTests,
    devApiMode,
    // constants
    cartCapacity,
    // getters
    token,
    getTokens,
    loyaltyFor,
    logBan,
    abTestSettings,
    gameCart,
    gameCartMap,
    gameCartMinVal,
    gameCartSumVal,
    gameCartSumValHuman,
    gameCartPaymentAvailable,
    // actions
    setToken,
    clearTokens,
    logOutAll,
    logOut,
    setLoyalty,
    setLogBan,
    setAbTests,
    addToGameCart,
    updateGameCartOffer,
    clearGameCart,
    removeFromGameCart,
    setCartGameMinVal
  }
}, { persist: !import.meta.env.SSR })
